import { RuleRender } from 'antd/lib/form';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const getAfterDateRule =
  (earlierDateKey: string, earlierDateLabel?: string): RuleRender =>
  form => ({
    validator: (_, value) => {
      const earlierDate = form.getFieldValue(earlierDateKey);

      if (
        earlierDate &&
        value &&
        dayjs(value).isSameOrBefore(dayjs(earlierDate))
      ) {
        return Promise.reject(
          new Error(
            `Must be after ${
              earlierDateLabel || earlierDateKey.replace(/_/g, ' ')
            }`,
          ),
        );
      }
      return Promise.resolve();
    },
  });

export const getSameOrBeforeDateRule =
  (laterDateKey: string, laterDateLabel?: string): RuleRender =>
  form => ({
    validator: (_, value) => {
      const laterDate = form.getFieldValue(laterDateKey);

      if (
        laterDate &&
        value &&
        !dayjs(dayjs(value)).isSameOrBefore(laterDate)
      ) {
        return Promise.reject(
          new Error(
            `Must be equal or before ${
              laterDateLabel || laterDateKey.replace(/_/g, ' ')
            }`,
          ),
        );
      }
      return Promise.resolve();
    },
  });

export const getSameOrAfterDateRule =
  (earlierDateKey: string, earlierDateLabel?: string): RuleRender =>
  form => ({
    validator: (_, value) => {
      const earlierDate = form.getFieldValue(earlierDateKey);

      if (
        earlierDate &&
        value &&
        !dayjs(value).isSameOrAfter(dayjs(earlierDate), 'day')
      ) {
        return Promise.reject(
          new Error(
            `Must be equal or after ${
              earlierDateLabel || earlierDateKey.replace(/_/g, ' ')
            }`,
          ),
        );
      }
      return Promise.resolve();
    },
  });

export const getEndDateAfterOrEqualStartRule = (): RuleRender => form => ({
  validator: (_, value) => {
    const startDate = form.getFieldValue('start_date');
    const endDate = value;

    if (!startDate || !endDate) {
      return Promise.resolve();
    }

    if (dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
      return Promise.reject(new Error('End date cannot be before start date'));
    }

    return Promise.resolve();
  },
});

export const getStartTimeBeforeEndRule = (): RuleRender => form => ({
  validator: (_, value) => {
    const startDate = form.getFieldValue('start_date');
    const endDate = form.getFieldValue('end_date');
    const startTime = value;
    const endTime = form.getFieldValue('end_time');

    if (!startDate || !endDate || !startTime || !endTime) {
      return Promise.resolve();
    }

    if (!dayjs(startDate).isSame(dayjs(endDate), 'day')) {
      return Promise.resolve();
    }

    const startDateTime = dayjs(startDate)
      .hour(parseInt(startTime.split(':')[0], 10))
      .minute(parseInt(startTime.split(':')[1], 10));

    const endDateTime = dayjs(endDate)
      .hour(parseInt(endTime.split(':')[0], 10))
      .minute(parseInt(endTime.split(':')[1], 10));

    if (endDateTime.isSameOrBefore(startDateTime)) {
      return Promise.reject(
        new Error('Start time must be before end time when dates are the same'),
      );
    }

    return Promise.resolve();
  },
});

export const getEndDateTimeAfterStartRule = (): RuleRender => form => ({
  validator: (_, value) => {
    const startDate = form.getFieldValue('start_date');
    const startTime = form.getFieldValue('start_time');
    const endDate = form.getFieldValue('end_date');
    const endTime = value;

    if (!startDate || !startTime || !endDate || !endTime) {
      return Promise.resolve();
    }

    const startDateTime = dayjs(startDate)
      .hour(parseInt(startTime.split(':')[0], 10))
      .minute(parseInt(startTime.split(':')[1], 10));

    const endDateTime = dayjs(endDate)
      .hour(parseInt(endTime.split(':')[0], 10))
      .minute(parseInt(endTime.split(':')[1], 10));

    if (endDateTime.isSameOrBefore(startDateTime)) {
      return Promise.reject(
        new Error('End date and time must be after start date and time'),
      );
    }

    return Promise.resolve();
  },
});
